import type { Icon } from "@phosphor-icons/react";
import {
  ArrowCounterClockwise,
  ArrowsClockwise,
  ArrowsLeftRight,
  Bell,
  BellRinging,
  Brain,
  Briefcase,
  Broadcast,
  Buildings,
  CalendarPlus,
  ClockCounterClockwise,
  DownloadSimple,
  EnvelopeSimple,
  Files,
  Flask,
  FlowArrow,
  GitBranch,
  Globe,
  Heartbeat,
  IdentificationCard,
  Key,
  ListChecks,
  LockKey,
  Palette,
  PaperPlaneTilt,
  Play,
  PlugsConnected,
  Robot,
  Scales,
  ShareNetwork,
  ShieldCheck,
  Sparkle,
  Terminal,
  Translate,
  UploadSimple,
  User,
  Users,
  UsersThree,
  WhatsappLogo,
} from "@phosphor-icons/react";
import type { ComponentType } from "react";
import { ABILITY, type Abilities } from "~/api/types";
import { DocitAuthSection } from "~/features/governance/DocitAuthSection";
import { OrganizationsSection } from "~/features/governance/OrganizationsSection";
import { PoliciesSection } from "~/features/governance/PoliciesSection";
import { RolesSection } from "~/features/governance/RolesSection";
import { Reminders } from "~/features/nudges/RemindersSection";
import { ProjectsSection } from "~/features/projects/ProjectsSection";
import { Activity } from "~/features/settings/sections/Activity";
import { Ai } from "~/features/settings/sections/Ai";
import { AiActions } from "~/features/settings/sections/AiActions";
import { AiPermissions } from "~/features/settings/sections/AiPermissions";
import { ApiKeys } from "~/features/settings/sections/ApiKeys";
import { Appearance } from "~/features/settings/sections/Appearance";
import { ConnectedIdes } from "~/features/settings/sections/ConnectedIdes";
import { DeliveryLedger } from "~/features/settings/sections/DeliveryLedger";
import { Details } from "~/features/settings/sections/Details";
import { DocumentationSync } from "~/features/settings/sections/DocumentationSync";
import { Export } from "~/features/settings/sections/Export";
import { Features } from "~/features/settings/sections/Features";
import { FlowHealth } from "~/features/settings/sections/FlowHealth";
import { Flows } from "~/features/settings/sections/Flows";
import { GitRepositories } from "~/features/settings/sections/GitRepositories";
import { Groups } from "~/features/settings/sections/Groups";
import { Import } from "~/features/settings/sections/Import";
import { Language } from "~/features/settings/sections/Language";
import { Mail } from "~/features/settings/sections/Mail";
import { Members } from "~/features/settings/sections/Members";
import { MyWork } from "~/features/settings/sections/MyWork";
import { Notifications } from "~/features/settings/sections/Notifications";
import { Placeholder } from "~/features/settings/sections/Placeholder";
import { Profile } from "~/features/settings/sections/Profile";
import { RemoteAccess } from "~/features/settings/sections/RemoteAccess";
import { RemoteSessions } from "~/features/settings/sections/RemoteSessions";
import { Roles } from "~/features/settings/sections/Roles";
import { Runs } from "~/features/settings/sections/Runs";
import { Security } from "~/features/settings/sections/Security";
import { Shares } from "~/features/settings/sections/Shares";
import { TaskCenter } from "~/features/settings/sections/TaskCenter";
import { Tasks } from "~/features/settings/sections/Tasks";
import { Telegram } from "~/features/settings/sections/Telegram";
import { Templates } from "~/features/settings/sections/Templates";
import { Thinkers } from "~/features/settings/sections/Thinkers";
import { WhatsApp } from "~/features/settings/sections/WhatsApp";
import { WebhooksSection } from "~/features/webhooks/WebhooksSection";
import { WebSourcesSection } from "~/features/websources/WebSourcesSection";
import { Channels } from "./sections/Channels";

/**
 * The settings surface as data. One route (`/settings/$section`) renders all of it, the
 * settings sidebar renders the same list, and the command bar can jump to any section —
 * exactly the arrangement the nav registry uses for the main navigation.
 */
export interface SettingsSection {
  /** URL slug: /settings/<id> */
  id: string;
  /** Natural-language translation keys, resolved with `t()` at the call site. */
  title: string;
  description: string;
  icon: Icon;
  groupId: string;
  keywords?: string;
  /** Gated on the workspace's server-supplied abilities. */
  visible?: (abilities: Abilities) => boolean;
  /**
   * Tabular sections — a run ledger, a flow list — need more than the reading measure the
   * prose-shaped sections are set to. The scene reads this; nothing else does.
   */
  wide?: boolean;
  component: ComponentType;
}

export interface SettingsGroupDefinition {
  id: string;
  title: string;
}

/**
 * Channels (2026-10-17, owner: "conceptualize the integrations so it is input channels and output
 * channels"): what comes IN (mail, Telegram texts, web sources), how Docit reaches you OUT (the bell,
 * e-mail, Telegram, webhooks), and the workspaces it is CONNECTED to (editor windows, repositories,
 * documentation folders, apps). The flat "Integrations" group is gone; nothing lost a route.
 */
export const settingsGroups: SettingsGroupDefinition[] = [
  { id: "account", title: "Account" },
  { id: "workspace", title: "Workspace" },
  { id: "channels-in", title: "Input channels" },
  { id: "channels-out", title: "Output channels" },
  { id: "connected", title: "Connected workspaces" },
  { id: "automation", title: "Automation" },
  { id: "governance", title: "Governance" },
];

const canAdminister = (abilities: Abilities) => abilities[ABILITY.workspaceUpdate] === true;
const canManageMembers = (abilities: Abilities) =>
  abilities[ABILITY.workspaceManageMembers] === true;
const canManageApiKeys = (abilities: Abilities) =>
  abilities[ABILITY.workspaceManageApiKeys] === true;
const canManageIntegrations = (abilities: Abilities) =>
  abilities[ABILITY.workspaceManageIntegrations] === true;
const canViewAudit = (abilities: Abilities) => abilities[ABILITY.workspaceViewAudit] === true;

/** A scaffolded section: the route, the gate and the chrome are real; the body is not yet. */
function scaffold(note: string): ComponentType {
  return function ScaffoldedSection() {
    return <Placeholder note={note} />;
  };
}

export const settingsSections: SettingsSection[] = [
  // ── Account ────────────────────────────────────────────────────────────────
  {
    id: "profile",
    title: "Profile",
    description: "Your name, photo and how you appear to other people.",
    icon: User,
    groupId: "account",
    keywords: "name avatar photo account me",
    component: Profile,
  },
  {
    id: "my-work",
    title: "My work",
    description: "The teams you belong to and the work assigned to you.",
    icon: Briefcase,
    groupId: "account",
    keywords: "tasks assigned team membership mine due",
    component: MyWork,
  },
  {
    id: "appearance",
    title: "Appearance",
    description: "Choose how Slate looks on this device.",
    icon: Palette,
    groupId: "account",
    keywords: "theme dark light system colour color",
    component: Appearance,
  },
  {
    id: "language",
    title: "Language",
    description: "Interface language and reading direction.",
    icon: Translate,
    groupId: "account",
    keywords: "locale hebrew english rtl translation",
    component: Language,
  },
  {
    // Account, not Automation, and deliberately ungated: these are one person's prompts and
    // one person's display preferences, the way kms had it. A member with no admin rights
    // still gets to decide what the AI offers them.
    id: "ai-actions",
    title: "AI actions",
    description: "What the AI offers where you select text, and how results are shown.",
    icon: Sparkle,
    groupId: "account",
    keywords: "selection actions prompts transform rewrite summarize shortcuts provider",
    wide: true,
    component: AiActions,
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Decide what reaches you, and where.",
    icon: Bell,
    groupId: "channels-out",
    keywords: "email alerts digest",
    component: Notifications,
  },
  {
    // Docit Work 05 §8 — questions for me in Telegram, answered with a tap or a reply.
    id: "telegram",
    title: "Telegram",
    description: "Get questions in Telegram and answer them there.",
    icon: PaperPlaneTilt,
    groupId: "channels-out",
    keywords: "telegram chat phone mobile messenger bot",
    component: Telegram,
  },
  {
    // Docit Work 05 §10 — questions for me in WhatsApp (Meta Cloud API), answered with a tap or a reply.
    id: "whatsapp",
    title: "WhatsApp",
    description: "Get questions in WhatsApp and answer them there.",
    icon: WhatsappLogo,
    groupId: "channels-out",
    keywords: "whatsapp chat phone mobile messenger meta",
    component: WhatsApp,
  },
  {
    // Channels (2026-10-17): the one page that says what comes in, how Docit reaches you, and where each is set up.
    id: "channels",
    title: "Channels",
    description:
      "Everything that comes in and every way Docit reaches you — the whole picture on one page.",
    icon: ArrowsLeftRight,
    groupId: "channels-in",
    keywords: "channels input output telegram mail whatsapp slack webhooks bell email",
    component: Channels,
  },
  {
    // Mail connections (2026-10-16) — MY Gmail / Outlook: knowledge, tiles, reply-to-answer, send as me.
    id: "mail",
    title: "Mail",
    description: "Connect your Gmail or Outlook mailbox.",
    icon: EnvelopeSimple,
    groupId: "channels-in",
    keywords: "mail email gmail outlook microsoft google inbox mailbox",
    component: Mail,
  },
  {
    // W7 D5 — `nudges.create` had four sibling endpoints and no reader; this is the reader.
    id: "reminders",
    title: "Reminders",
    description: "Everything you asked to be reminded about — snooze, complete or cancel.",
    icon: BellRinging,
    groupId: "account",
    keywords: "nudge reminder snooze due later quickwhen",
    component: Reminders,
  },
  {
    id: "personal-api-keys",
    title: "Personal API keys",
    description: "Keys that act as you. Treat them like passwords.",
    icon: Key,
    groupId: "account",
    keywords: "token secret personal access",
    component: ApiKeys,
  },

  // ── Workspace ──────────────────────────────────────────────────────────────
  {
    id: "details",
    title: "Workspace details",
    description: "Name, URL and branding for this workspace.",
    icon: Buildings,
    groupId: "workspace",
    keywords: "name subdomain logo branding",
    visible: canAdminister,
    component: Details,
  },
  {
    id: "members",
    title: "Members",
    description: "Who has access, and at what level.",
    icon: Users,
    groupId: "workspace",
    keywords: "people users invite roles suspend",
    visible: canManageMembers,
    component: Members,
  },
  {
    id: "groups",
    title: "Groups",
    description: "Bundle people together and grant access once.",
    icon: UsersThree,
    groupId: "workspace",
    keywords: "teams permissions access",
    visible: canManageMembers,
    component: Groups,
  },
  {
    id: "shares",
    title: "Shared links",
    description: "Every document currently reachable without signing in.",
    icon: ShareNetwork,
    groupId: "workspace",
    keywords: "public links revoke external",
    visible: canAdminister,
    component: Shares,
  },
  {
    id: "templates",
    title: "Templates",
    description: "Starting points people can pick when creating a document.",
    icon: Files,
    groupId: "workspace",
    keywords: "boilerplate starter document",
    visible: canAdminister,
    component: Templates,
  },
  {
    id: "import",
    title: "Import",
    description: "Bring documents in from another tool.",
    icon: UploadSimple,
    groupId: "workspace",
    keywords: "migrate upload markdown",
    visible: canAdminister,
    component: Import,
  },
  {
    id: "export",
    title: "Export",
    description: "Take everything with you, any time.",
    icon: DownloadSimple,
    groupId: "workspace",
    keywords: "download backup markdown json",
    visible: canAdminister,
    component: Export,
  },
  {
    id: "security",
    title: "Security",
    description: "Sign-in requirements and workspace-wide restrictions.",
    icon: ShieldCheck,
    groupId: "workspace",
    keywords: "sso domains sessions 2fa",
    visible: canAdminister,
    component: Security,
  },
  {
    id: "features",
    title: "Features",
    description: "Turn optional capabilities on for everyone.",
    icon: Flask,
    groupId: "workspace",
    keywords: "flags beta experiments",
    visible: canAdminister,
    component: Features,
  },
  {
    // W4: findable from settings search, but the console itself is a ROUTE — watching is not
    // configuring, and two copies of the window list is one copy too many.
    id: "connected-ides",
    title: "Connected IDEs",
    description: "The editor windows signed in to this workspace.",
    icon: Terminal,
    groupId: "connected",
    keywords: "ide vscode editor windows sessions machine",
    component: ConnectedIdes,
  },
  {
    id: "remote-access",
    title: "Remote IDE access",
    description: "Temporary, scoped access to a teammate's editor.",
    icon: LockKey,
    groupId: "connected",
    keywords: "grant permission machine remote colleague scope expiry",
    // Deliberately UNGATED: everyone must be able to see and kill access they have given,
    // and an admin gate here would hide a person's own grants from them.
    component: RemoteAccess,
  },
  {
    id: "remote-sessions",
    title: "Remote sessions",
    description: "Ask a connected editor to do something, and watch it answer.",
    icon: Terminal,
    groupId: "connected",
    keywords: "dispatch prompt discussion task ide approve",
    wide: true,
    component: RemoteSessions,
  },
  {
    id: "api-keys",
    title: "Workspace API keys",
    description: "Keys that act on behalf of the workspace.",
    icon: LockKey,
    groupId: "workspace",
    keywords: "token secret service integration",
    visible: canManageApiKeys,
    component: ApiKeys,
  },

  // ── Integrations ───────────────────────────────────────────────────────────
  {
    id: "authentication",
    title: "Authentication",
    description: "Docit.Auth identity, teams and single sign-on.",
    icon: IdentificationCard,
    groupId: "workspace",
    keywords: "sso oidc identity auth teams sync abac",
    visible: canManageIntegrations,
    component: DocitAuthSection,
  },
  {
    id: "connected-apps",
    title: "Connected apps",
    description: "Tools that have been granted access to this workspace.",
    icon: PlugsConnected,
    groupId: "connected",
    keywords: "oauth apps integrations connections editors",
    visible: canManageIntegrations,
    component: scaffold(
      "Lists third-party grants once there is an OAuth authorisation server to list them from.",
    ),
  },
  {
    id: "webhooks",
    title: "Webhooks",
    description: "Push events to your own endpoints.",
    icon: Broadcast,
    groupId: "channels-out",
    keywords: "events http callbacks subscriptions deliveries secret",
    visible: canManageIntegrations,
    wide: true,
    component: WebhooksSection,
  },
  {
    id: "git-repositories",
    title: "Git repositories",
    description:
      "Connect a repository to the workspace or a project; its structure shows in the sidebar.",
    icon: GitBranch,
    groupId: "connected",
    keywords: "git github repository repo source code mount token app connection tree history",
    visible: canAdminister,
    wide: true,
    component: GitRepositories,
  },
  {
    id: "documentation-sync",
    title: "Documentation sync",
    description: "Markdown folders that keep a collection in step.",
    icon: ArrowsClockwise,
    groupId: "connected",
    keywords: "git markdown folder sync ai-map link clone drift",
    visible: canAdminister,
    wide: true,
    component: DocumentationSync,
  },
  {
    id: "delivery-ledger",
    title: "Delivery Ledger",
    description: "Who was told what, when, and on which channel.",
    icon: EnvelopeSimple,
    groupId: "connected",
    keywords: "notifications email delivery audit sent failed skipped",
    visible: canViewAudit,
    wide: true,
    component: DeliveryLedger,
  },

  // ── Automation ─────────────────────────────────────────────────────────────
  {
    // The slug stays `agents` on purpose: it is a live URL, and renaming a section is not
    // worth breaking every bookmark and every link that already points at it.
    id: "agents",
    title: "Thinkers",
    description: "The AI personas, teammates and local models this workspace can address.",
    icon: Robot,
    groupId: "automation",
    keywords: "ai models thinkers agents assistants personas local ollama share binding skills",
    // A member links their own compute here. Workspace edits retain their individual server abilities.
    wide: true,
    component: Thinkers,
  },
  {
    id: "ai-permissions",
    title: "AI permissions",
    description: "Which skills and tools each kind of thinker may use.",
    icon: ShieldCheck,
    groupId: "automation",
    keywords: "capabilities rbac skills mcp tools allow deny policy permissions",
    visible: canAdminister,
    wide: true,
    component: AiPermissions,
  },
  {
    id: "flows",
    title: "Flows",
    description: "Multi-step automations, and who may run them.",
    icon: FlowArrow,
    groupId: "automation",
    keywords: "workflow automation orchestration steps versions run",
    visible: canAdminister,
    wide: true,
    component: Flows,
  },
  {
    id: "task-center",
    title: "Task Center",
    description: "Schedule a flow, a digest, or work for a connected editor.",
    icon: CalendarPlus,
    groupId: "automation",
    keywords: "schedule cron digest recurring trigger interval ide flow",
    visible: canAdminister,
    wide: true,
    component: TaskCenter,
  },
  {
    id: "flow-health",
    title: "Flow Health",
    description: "What automation costs this workspace, and how failures are announced.",
    icon: Heartbeat,
    groupId: "automation",
    keywords: "billing usage tokens storage retention failures notify",
    visible: canAdminister,
    wide: true,
    component: FlowHealth,
  },
  {
    id: "runs",
    title: "Runs",
    description: "What has executed, what it cost, and what it produced.",
    icon: Play,
    groupId: "automation",
    keywords:
      "history executions observability run center approve pause debugger step jump breakpoints artifacts publish steering degraded",
    visible: canViewAudit,
    wide: true,
    component: Runs,
  },
  {
    // Thinker fabric §1.2 — who the project gives work to. Beside AI because it is the same question
    // one layer up: AI says what the models cost, this says which of them a named role runs on.
    // Id `thinker-roles`, NOT `roles`: that id (and /settings/roles) already belongs to the governance
    // RBAC Roles section below — two sections with one id made the route ambiguous (2026-10-05).
    id: "thinker-roles",
    title: "Roles & thinkers",
    description: "Who this project gives work to, and which thinker runs each role.",
    icon: Brain,
    groupId: "automation",
    keywords:
      "roles thinkers models provider effort branch scope area dev analyst qa reviewer agnostic",
    visible: canViewAudit,
    wide: true,
    component: Roles,
  },
  {
    id: "ai",
    title: "AI",
    description: "Every model call the workspace made, and what it cost.",
    icon: Brain,
    groupId: "automation",
    keywords: "usage tokens cost spend models llm calls ledger queue digestion",
    visible: canViewAudit,
    wide: true,
    component: Ai,
  },
  {
    // The URL slug stays `tasks` for stability; the surface itself is now the machinery
    // front door (W2 §A.1) — schedules, runs and dispatches in one place, not just schedules.
    id: "tasks",
    title: "Tasks",
    description:
      "Everything the machinery is doing: schedules, runs, and work handed to an editor.",
    icon: ListChecks,
    groupId: "automation",
    keywords: "cron schedule recurring jobs trigger health worker dispatch spend stalled approval",
    visible: canAdminister,
    wide: true,
    component: Tasks,
  },
  {
    id: "activity",
    title: "Activity",
    description: "A chronological record of what changed and who changed it.",
    icon: ClockCounterClockwise,
    groupId: "automation",
    keywords: "audit log events timeline history",
    visible: canViewAudit,
    wide: true,
    component: Activity,
  },
  {
    id: "web-sources",
    title: "Web sources",
    description: "Where thinkers may read from, and how much.",
    icon: Globe,
    groupId: "channels-in",
    keywords: "crawl search engine domains allowlist approvals internet",
    visible: canAdminister,
    component: WebSourcesSection,
  },

  // ── Governance ─────────────────────────────────────────────────────────────
  {
    id: "organizations",
    title: "Organizations",
    description: "The tenants this workspace belongs to.",
    icon: Briefcase,
    groupId: "governance",
    keywords: "tenants companies hierarchy billing plan",
    visible: canAdminister,
    component: OrganizationsSection,
  },
  {
    id: "projects",
    title: "Projects",
    description: "Group work so access and reporting follow it.",
    icon: Files,
    groupId: "governance",
    keywords: "programmes initiatives grouping teams anchor collections",
    visible: canAdminister,
    wide: true,
    component: ProjectsSection,
  },
  {
    id: "roles",
    title: "Roles",
    description: "Named bundles of abilities, assigned to people and groups.",
    icon: Scales,
    groupId: "governance",
    keywords: "rbac permissions abilities access control",
    visible: canManageMembers,
    component: RolesSection,
  },
  {
    id: "policies",
    title: "Policies",
    description: "Rules the server enforces on every request.",
    icon: ShieldCheck,
    groupId: "governance",
    keywords: "rules governance compliance retention",
    visible: canAdminister,
    component: PoliciesSection,
  },
  {
    id: "audit-log",
    title: "Audit log",
    description: "An append-only record for compliance review.",
    icon: ArrowCounterClockwise,
    groupId: "governance",
    keywords: "compliance security events export",
    visible: canViewAudit,
    component: scaffold("Append-only, exportable, and never editable from the UI."),
  },
];

export function findSettingsSection(id: string): SettingsSection | undefined {
  return settingsSections.find((section) => section.id === id);
}

export interface SettingsGroup extends SettingsGroupDefinition {
  sections: SettingsSection[];
}

/** Groups in declaration order, with sections the viewer may not see removed. */
export function visibleSettingsGroups(abilities: Abilities): SettingsGroup[] {
  return settingsGroups
    .map((group) => ({
      ...group,
      sections: settingsSections.filter(
        (section) =>
          section.groupId === group.id && (section.visible ? section.visible(abilities) : true),
      ),
    }))
    .filter((group) => group.sections.length > 0);
}

export function firstVisibleSection(abilities: Abilities): SettingsSection | undefined {
  return visibleSettingsGroups(abilities)[0]?.sections[0];
}
